import { useState } from 'react';
import { AlertCircle, CheckCircle2, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const InstantlyIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);

  const handleConnect = (event: React.FormEvent) => {
    event.preventDefault();
    setIsConnected(true);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="flex-1 p-8">
        <header className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Instantly.ai Integration</h1>
          <p className="text-gray-600">Connect your account to leverage powerful email automation tools.</p>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Connect to Instantly.ai</CardTitle>
              <CardDescription>Enter your API credentials to establish the connection.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleConnect} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="api-key">API Key</Label>
                  <Input id="api-key" type="password" placeholder="Enter your Instantly.ai API key" required />
                </div>
                <Button type="submit" className="w-full">
                  {isConnected ? 'Connected' : 'Connect'} <Zap className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </CardContent>
            <CardFooter className="justify-between">
              <p className="text-sm text-gray-500">Don't have an API key?</p>
              <Button variant="outline">Get API Key</Button>
            </CardFooter>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent>
                {isConnected ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Connected
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Not Connected
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integration Benefits</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600 list-disc list-inside">
                  <li>Automated email campaigns</li>
                  <li>Advanced analytics</li>
                  <li>Personalized content delivery</li>
                  <li>Seamless workflow integration</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="mb-4 text-2xl font-semibold">How It Works</h2>
          <Tabs defaultValue="setup" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="setup">Setup</TabsTrigger>
              <TabsTrigger value="configure">Configure</TabsTrigger>
              <TabsTrigger value="use">Use</TabsTrigger>
            </TabsList>
            <TabsContent value="setup" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">1. Setup Your Account</h3>
              <p>Create an account on Instantly.ai and obtain your API credentials from the dashboard.</p>
            </TabsContent>
            <TabsContent value="configure" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">2. Configure Integration</h3>
              <p>Enter your API key in the form above to establish the connection between platforms.</p>
            </TabsContent>
            <TabsContent value="use" className="p-4 bg-white rounded-b-lg shadow">
              <h3 className="mb-2 font-semibold">3. Start Automating</h3>
              <p>Once connected, you can start creating and managing email campaigns directly from your dashboard.</p>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <aside className="hidden w-64 p-6 bg-white border-l border-gray-200 lg:block">
        <h2 className="mb-4 text-lg font-semibold">Related Actions</h2>
        <nav className="space-y-2">
          <a href="#" className="block text-blue-600 hover:underline">
            View Documentation
          </a>
          <a href="#" className="block text-blue-600 hover:underline">
            Contact Support
          </a>
          <a href="#" className="block text-blue-600 hover:underline">
            Explore Templates
          </a>
        </nav>
        <div className="mt-8">
          <Badge variant="outline" className="mb-2">
            Pro Tip
          </Badge>
          <p className="text-sm text-gray-600">
            Use webhooks to sync data in real-time between your app and Instantly.ai.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default InstantlyIntegration;
